import { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  getNavicStatus,
  listRequests,
  promoteToNavic,
  searchNavicVolunteers,
} from '../services/api';
import type {
  NavicStatus,
  NavicVolunteer,
  WorkgroupRequest,
} from '../types/api';

// Tab 2 — HRP Staffing. Requests submitted to HRP live on the left; the right
// panel searches verified volunteers in NAVIC, which owns the volunteer
// entities. Promoting a request creates its Workgroup in NAVIC and moves the
// request to 'staffing'; assignment of volunteers to seats happens in NAVIC's
// FD workbench today.
export function StaffingTab({ onChange }: { onChange?: () => void }) {
  const { user } = useAuth();
  const isFd = user?.role === 'fd';

  const [requests, setRequests] = useState<WorkgroupRequest[]>([]);
  const [loading, setLoading] = useState(false);
  const [navicStatus, setNavicStatus] = useState<NavicStatus | null>(null);
  const [promoteMessage, setPromoteMessage] = useState<string | null>(null);
  const [promotingId, setPromotingId] = useState<string | null>(null);

  const refresh = async () => {
    setLoading(true);
    try {
      const rows = await listRequests();
      setRequests(
        rows.filter((r) => r.status === 'submitted_to_hrp' || r.status === 'staffing'),
      );
      onChange?.();
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void refresh();
    getNavicStatus()
      .then(setNavicStatus)
      .catch(() =>
        setNavicStatus({ configured: false, reachable: false, baseUrl: null }),
      );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePromote = async (r: WorkgroupRequest) => {
    if (
      !confirm(
        `Create the workgroup "${r.projectName || 'this request'}" in NAVIC and move it to staffing?`,
      )
    )
      return;
    setPromotingId(r.id);
    setPromoteMessage(null);
    try {
      const { navicWorkgroup } = await promoteToNavic(r.id);
      setPromoteMessage(`Created NAVIC workgroup "${navicWorkgroup.name}".`);
      await refresh();
    } catch {
      setPromoteMessage(
        'Promotion failed — check that NAVIC is reachable and the request is still awaiting staffing.',
      );
    } finally {
      setPromotingId(null);
    }
  };

  return (
    <div className="row g-3">
      {promoteMessage && (
        <div className="col-12">
          <div className="alert alert-info py-2 mb-0">{promoteMessage}</div>
        </div>
      )}

      <div className="col-md-6">
        <div className="card">
          <div className="card-header">
            <strong>Requests awaiting staffing</strong>
            {loading && <span className="text-secondary ms-2">(refreshing…)</span>}
          </div>
          <div className="list-group list-group-flush">
            {requests.length === 0 && !loading && (
              <div className="list-group-item text-secondary">
                Nothing here yet — requests appear after FD submits them to HRP.
              </div>
            )}
            {requests.map((r) => (
              <div className="list-group-item" key={r.id}>
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <strong>{r.projectName || '(no name)'}</strong>
                    <span
                      className={`badge ms-2 ${
                        r.status === 'staffing' ? 'bg-green-lt' : 'bg-blue-lt'
                      }`}
                    >
                      {r.status === 'staffing' ? 'staffing' : 'awaiting review'}
                    </span>
                    <div className="text-secondary small">
                      {r.membersNeeded
                        ? `${r.membersNeeded} members needed`
                        : 'member count not specified'}
                    </div>
                  </div>
                  {isFd && r.status === 'submitted_to_hrp' && (
                    <button
                      type="button"
                      className="btn btn-sm btn-primary"
                      disabled={!navicStatus?.reachable || promotingId === r.id}
                      title={
                        navicStatus?.reachable
                          ? 'Create this workgroup in NAVIC'
                          : 'NAVIC is not connected'
                      }
                      onClick={() => handlePromote(r)}
                    >
                      {promotingId === r.id ? 'Promoting…' : 'Promote to NAVIC'}
                    </button>
                  )}
                  {r.navicWorkgroupId && (
                    <span className="badge bg-green-lt">in NAVIC</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="col-md-6">
        <VolunteerPanel navicStatus={navicStatus} />
      </div>
    </div>
  );
}

function VolunteerPanel({ navicStatus }: { navicStatus: NavicStatus | null }) {
  const [query, setQuery] = useState('');
  const [volunteers, setVolunteers] = useState<NavicVolunteer[]>([]);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!navicStatus?.reachable) return;
    let cancelled = false;
    setSearching(true);
    setError(null);
    const handle = setTimeout(async () => {
      try {
        const rows = await searchNavicVolunteers(query.trim());
        if (!cancelled) setVolunteers(rows);
      } catch {
        if (!cancelled) setError('Volunteer search failed — is NAVIC up?');
      } finally {
        if (!cancelled) setSearching(false);
      }
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [query, navicStatus?.reachable]);

  if (!navicStatus) {
    return (
      <div className="card">
        <div className="card-body text-secondary">Checking NAVIC connection…</div>
      </div>
    );
  }

  if (!navicStatus.configured) {
    return (
      <div className="card">
        <div className="card-body">
          <h3 className="h4">Verified volunteers</h3>
          <p className="text-secondary mb-0">
            NAVIC is not configured. Set <code>NAVIC_API_URL</code> and{' '}
            <code>NAVIC_API_TOKEN</code> to search verified volunteers here.
          </p>
        </div>
      </div>
    );
  }

  if (!navicStatus.reachable) {
    return (
      <div className="card">
        <div className="card-body">
          <h3 className="h4">Verified volunteers</h3>
          <p className="text-danger mb-0">
            NAVIC at {navicStatus.baseUrl} is not responding.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="card-header">
        <strong>Verified volunteers</strong>
        <span className="text-secondary ms-2 small">from NAVIC</span>
      </div>
      <div className="card-body py-2">
        <input
          type="search"
          className="form-control"
          placeholder="Search by name, email, or skill…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </div>
      {error && <div className="card-body py-2 text-danger">{error}</div>}
      <div
        className="list-group list-group-flush"
        style={{ maxHeight: '60vh', overflowY: 'auto' }}
      >
        {searching && (
          <div className="list-group-item text-secondary">Searching…</div>
        )}
        {!searching && volunteers.length === 0 && !error && (
          <div className="list-group-item text-secondary">
            No verified volunteers matched.
          </div>
        )}
        {!searching &&
          volunteers.map((v) => (
            <div className="list-group-item" key={v.id}>
              <div className="d-flex justify-content-between">
                <strong>
                  {v.firstName} {v.lastName}
                </strong>
                <span className="text-secondary small">
                  {v.areaName ?? v.city ?? ''}
                </span>
              </div>
              <div className="text-secondary small">
                {[v.email, v.phone].filter(Boolean).join(' · ') || 'no contact info'}
              </div>
              {v.skills.length > 0 && (
                <div className="mt-1">
                  {v.skills.slice(0, 6).map((s) => (
                    <span className="badge bg-blue-lt me-1" key={s}>
                      {s}
                    </span>
                  ))}
                </div>
              )}
            </div>
          ))}
      </div>
    </div>
  );
}
